import { useCallback } from 'react';

import { useTouchCareerLessonMutation } from 'core/graphql';

/**
 * Marks an unlocked career lesson as OPENED / «просмотрен» (spec 0020 Фаза B).
 *
 * Fire-and-forget engagement telemetry: the page calls `markOpened` exactly once
 * when an unlocked lesson mounts (guarded by a ref keyed on the lesson id). A
 * failed ping must never surface to the student or block the lesson, so the
 * rejection is swallowed. Opening a lesson does NOT unlock the next one — the
 * completion gate stays `CareerLessonProgress` (spec 0020 §3.1).
 *
 * The `markCareerLessonOpened` payload returns only `openCount` (not the lesson
 * entity), so the list's cached `isOpened` is refreshed the normal way: the list
 * uses `cache-and-network`, so returning to it re-reads the freshly-opened state.
 */
export const useMarkCareerLessonOpened = () => {
  const [mutate] = useTouchCareerLessonMutation();

  const markOpened = useCallback(
    (lessonId: string): void => {
      // Fire-and-forget: the caught rejection keeps a failed ping from surfacing.
      mutate({ variables: { lessonId } }).catch(() => undefined);
    },
    [mutate],
  );

  return { markOpened };
};
